/**
 * useHelpSearchIndex — index de recherche LÉGER du Help Drawer (HELP-DRAWER §4.1).
 *
 * Charge une seule fois par session (lazy, au 1er focus du champ) l'index complet
 * des articles de la LANGUE active depuis `GET /knowledge/search-index` (sans le
 * corps), puis pré-normalise les champs cherchables (repli d'accents). La
 * recherche elle-même reste 100 % locale (cf. `helpSearch.ts`).
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { API_BASE, apiFetch } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'
import { buildFoldedItems, type FoldedHelpItem, type HelpSearchItem } from '@/lib/helpSearch'

async function fetchSearchIndex(lang: string): Promise<HelpSearchItem[]> {
    const params = new URLSearchParams({ lang })
    const response = await apiFetch(`${API_BASE}/knowledge/search-index?${params}`)
    if (!response.ok) throw new Error('Failed to fetch help search index')
    const data = await response.json()
    return (data.items ?? []) as HelpSearchItem[]
}

export interface UseHelpSearchIndexReturn {
    /** Items pré-normalisés (repliés) prêts pour `searchArticles`. */
    items: FoldedHelpItem[]
    isLoading: boolean
    error: string | null
}

/**
 * @param enabled — déclenche le chargement (à passer `true` au 1er focus du champ).
 * @param scopeKey — clé de portée (id utilisateur, ou `'anon'`) : l'API restreint
 *   l'index aux articles publics pour un visiteur non authentifié ; on ne mutualise
 *   donc pas le cache entre une page publique et l'app connectée.
 */
export function useHelpSearchIndex(
    enabled: boolean,
    scopeKey = 'anon',
): UseHelpSearchIndexReturn {
    const { i18n } = useTranslation()
    const lang = i18n.language?.slice(0, 2) || 'fr'

    const { data, isLoading, isFetching, error } = useQuery({
        queryKey: queryKeys.help.searchIndex(lang, scopeKey),
        queryFn: () => fetchSearchIndex(lang),
        enabled,
        // L'index change rarement (rebuild au boot serveur) : long staleTime.
        staleTime: 30 * 60_000,
    })

    const items = useMemo(() => buildFoldedItems(data ?? []), [data])

    return {
        items,
        isLoading: isLoading || isFetching,
        error: error ? (error as Error).message : null,
    }
}

export default useHelpSearchIndex
